import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { GalleriaModule } from 'primeng/galleria';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';

import { HeroReport } from '../../../model/hero-report/hero-report.model';
import { UTMConversionService } from '../../../model/utm-conversion/utm-conversion.service';

import { CLASSIFICATION_OPTIONS } from '../../../../../ordnances';
import { AccountService } from '../../../model/account/account.service';
import { HeroReportImageStorageService } from '../../../model/hero-report-image/hero-report-image-storage.service';
import { Account } from '../../../model/account/account.model';
import { showsMgrs } from '../../../utilities/sreReport';

@Component({
  selector: 'app-hero-report-card',
  templateUrl: './hero-report-card.component.html',
  styleUrls: ['./hero-report-card.component.scss'],
  standalone: true,
  imports: [
    ButtonModule,
    GalleriaModule,
    CommonModule,
    TranslateModule,
    RouterModule
  ],
  providers: [UTMConversionService]
})
export class HeroReportCardComponent implements OnInit {
  @Input() heroReport!: HeroReport;
  @Input() canViewEod = false;
  @Input() account: Account;

  @Output() cardClicked = new EventEmitter<HeroReport>();

  images = [] as {
    source: string;
    alt: string;
    title: string;
    createDate?: string;
  }[];

  currentImageIndex = 0;
  coordinates: string | null = null;

  classificationLabels = [];

  constructor(
    private utmConversionService: UTMConversionService,
    private accountService: AccountService,
    private imageStorage: HeroReportImageStorageService
  ) {}

  ngOnInit() {
    if (this.heroReport) {
      this.coordinates = this.utmConversionService.useOrConvertCoordinates(
        this.heroReport.latitude,
        this.heroReport.longitude,
        this.account?.gpsUnit
      );

      this.loadImages();

      this.heroReport.possibleIdentification
        .forEach((identificationKey: string) => {
          const matched = CLASSIFICATION_OPTIONS.find(
            (ord: any) => ord.key === identificationKey.trim(),
          );
          if (matched) {
            this.classificationLabels.push(matched.label + ' ' + matched.size);
          }
        });
    }
  }

  /** Downloads the report's images from S3; the card shows them once all have loaded. */
  private async loadImages() {
    const heroReportImages = (this.heroReport.heroReportImages || [])
      .filter(heroReportImage => heroReportImage.type !== 'EOD');

    const loaded = await Promise.all(
      heroReportImages.map(async heroReportImage => ({
        source: await this.imageStorage.getObjectUrl(heroReportImage),
        alt: heroReportImage.type,
        title: heroReportImage.type,
        createDate: heroReportImage.createDate
      }))
    );

    this.images = loaded.filter(image => image.source);
  }

  /** An SRE report with an MGRS shows it where the coordinates would be. */
  get showsMgrs(): boolean {
    return showsMgrs(this.heroReport);
  }

  onCardClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    this.cardClicked.emit(this.heroReport);
  }

  get currentImage() {
    return this.images[this.currentImageIndex];
  }

  nextImage() {
    if (this.currentImageIndex < this.images.length - 1) {
      this.currentImageIndex++;
    }
  }

  previousImage() {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
    }
  }
}
